import React, { useState, useEffect } from 'react';
import { Tooltip, IconButton, Typography, Button, Grid, CircularProgress } from '@mui/material';
import { CustomQuillEditor, Dropzone } from 'components';
import { Field, useFormikContext, FieldArray, FieldProps } from 'formik';
import axios from 'axios';
import { MdAttachFile as AttachmentIcon, MdClose as CancelIcon, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { IMessage, messageSaveSchema } from '../../CustomerTicketDetailsContext';
import * as yup from 'yup';
import { useIntl } from 'react-intl';
import { LocalizationIds } from 'helpers/Localizations';
import { useSnackbar } from 'notistack';


interface IProps {
    ticketNumber: string
    editMessage?: IMessage
    onClose?: () => void
}

let MessageEditor = (props: IProps) => {
    let { enqueueSnackbar } = useSnackbar();
    let { formatMessage } = useIntl();

    let [showDropzone, setShowDropzone] = useState<boolean>(false);
    let [uploadingFiles, setUploadingFiles] = useState<string[]>([]);

    type SaveMessage = yup.InferType<typeof messageSaveSchema>;

    let form = useFormikContext<SaveMessage>();


    useEffect(() => {
        if (props.editMessage) {
            form.setFieldValue('messageId', props.editMessage.messageId);
            form.setFieldValue('text', props.editMessage.value);
            form.setFieldValue('attachments', props.editMessage.attachments);
        }
    }, [props.editMessage])


    let dropCallback = (files: any[]) => {
        let filesInUpload = files.map(x => x.name);
        setUploadingFiles(filesInUpload);

        files.forEach(async (element: any) => {
            try {
                let formData = new FormData();
                formData.append("file", element);
                formData.append("ticketNumber", props.ticketNumber);

                let res = await axios.post('/api/CustomerTicketMessage/UploadAttachment', formData);

                let found = form.values.attachments.find(x => x.fileId === res.data.fileId);

                if (found) {
                    enqueueSnackbar(formatMessage({ id: LocalizationIds.INFO_AttachmentDuplicated }), { variant: 'warning' });
                    return;
                }

                form.values.attachments.push({
                    fileId: res.data.fileId,
                    fileName: element.name
                });
                form.setFieldValue('attachments', [...form.values.attachments]);
            } catch (err) {
                form.setFieldError("attachments", formatMessage({ id: LocalizationIds.ERR_FILE_IMPORT }));
            } finally {
                filesInUpload.splice(filesInUpload.indexOf(element.name), 1);
                setUploadingFiles([...filesInUpload])
            }
        })
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Field name="text">
                        {({ field }: FieldProps) =>
                            <div data-text-editor="name">
                                <CustomQuillEditor
                                    value={field.value}
                                    onChange={field.onChange(field.name)}
                                />
                            </div>
                        }
                    </Field>
                </Grid>
                <Grid item xs>
                    <Button color="secondary" style={{ marginLeft: 0 }} variant="outlined" onClick={() => setShowDropzone(!showDropzone)}>
                        {showDropzone ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}&nbsp;<AttachmentIcon />
                    </Button>
                </Grid>
                {props.onClose !== undefined &&
                    <Grid item>
                        <Button
                            color="secondary"
                            variant="outlined"
                            disabled={uploadingFiles.length > 0}
                            onClick={props.onClose}
                        >
                            {formatMessage({ id: LocalizationIds.Cancel })}
                        </Button>
                    </Grid>
                }
                <Grid item>
                    <Button
                        variant="contained"
                        disabled={uploadingFiles.length > 0 || form.isSubmitting}
                        style={{ marginRight: 0 }}
                        onClick={() => form.submitForm()}
                    >
                        {formatMessage({ id: LocalizationIds.Save })}
                    </Button>
                </Grid>
            </Grid>

            {showDropzone && <Dropzone isMultiple={true} onDropCallback={dropCallback} />}

            {uploadingFiles.map((x, index) =>
                <React.Fragment key={index}>
                    <Typography>{formatMessage({ id: LocalizationIds.UploadingFile })}&nbsp;{x}&nbsp;<CircularProgress size={20} /></Typography>
                </React.Fragment>
            )}

            {form.values.attachments.length > 0 &&
                <FieldArray name="attachments">
                    {arrayHelper => form.values.attachments
                        .map((file: any, index: number) =>
                            <Typography key={index}>
                                {formatMessage({ id: LocalizationIds.UploadedFile })}: &nbsp;{file.fileName}
                                <Tooltip arrow title={formatMessage({ id: LocalizationIds.Remove })}>
                                    <IconButton
                                        size="small"
                                        onClick={() => arrayHelper.remove(index)}
                                    >
                                        <CancelIcon />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        )
                    }
                </FieldArray>
            }
        </>
    )
}

export default MessageEditor;
